import React from 'react';

import Select, { SelectProps } from 'antd/es/select';

import Autocomplete, { AutocompleteProps } from '@common/react/components/Forms/Autocomplete/Autocomplete';
import ItemsSelect from '@common/react/components/Core/ItemsSelect/ItemsSelect';
import { BaseParams } from '@common/react/objects/BaseParams';

import { Location } from '@app/objects/Location';
import { NullableLocation } from '@app/components/Pages/Admin/Reports/Base/BaseReport';
import { Links } from '@app/components/UI/Links/Links';
import { defaultOptions } from '@app/components/UI/Location/defaultOptions';

const defaultStorageName = 'locations';

const { Option } = Select;

interface Props {
	onChange: (evt: { currentTarget: { name: string, value: any } }, location?: Location | null) => void;
	asSelect?: boolean;
	withEye?: boolean;
	location?: Location | null;
	request?: string;
	params?: BaseParams;
	autocompleteProps?: Partial<AutocompleteProps>;
	selectProps?: Partial<Omit<SelectProps, 'onChange' | 'getPopupContainer'>>;
	onLocationsLoaded?: (locations: Array<Location>) => void;
	loadRequest?: string;
	withoutAllLocation?: boolean;
	param?: string;
	skipInitLoad?: boolean;
	locations?: Array<Location>;
	allValueName?: string;
	withNullableLocation?: boolean;
	nullableLocationName?: string;
	getPopupContainer?: (node: HTMLElement) => HTMLElement;
	withoutStore?: boolean;
	loading?: boolean;
	storeName?: string;
	pagination?: any;
	// additionalFilters?: BaseParams;
}

const LocationSelect: React.FC<Props> = ({
	asSelect = true, location, withEye, params, ...rest
}) => {
	const {
		selectProps,
		autocompleteProps,
		onChange,
		loadRequest = defaultOptions.loadRequest,
		withoutAllLocation,
		param = 'locationId',
		loading,
		locations: defaultLocations,
		onLocationsLoaded,
		storeName,
		withoutStore,
		skipInitLoad,
		allValueName = 'All Locations',
		pagination = defaultOptions.pagination,
		withNullableLocation = false,
		nullableLocationName = 'No location',
		// additionalFilters = { pageSize: 100 },
	} = rest;

	const component = asSelect
		? (
			<ItemsSelect<Location>
				{...defaultOptions}
				getOptionProps={(item) => ({
					value: item.id,
					item,
					children: item.nameEn,
				})}
				selectProps={{
					placeholder: 'Location...',
					...selectProps,
					value: selectProps?.value || +(params?.[param] || (withoutAllLocation ? 0 : -1)) || undefined,
					getPopupContainer: rest.getPopupContainer,
					onChange: (value, option: any) => onChange({ currentTarget: { name: param, value } }, option?.item || null),
				}}
				pagination={pagination}
				onLoad={(res) => onLocationsLoaded && onLocationsLoaded(res.list)}
				loading={loading}
				loadingPlaceholder="Location..."
				filters={{
					...defaultOptions.filters,
					...params,
					locationId: undefined,
				}}
				items={defaultLocations}
				storeName={storeName || (withoutStore ? undefined : defaultStorageName)}
				loadRequest={loadRequest}
				skipInitLoad={skipInitLoad}
				additionalOptions={[
					withoutAllLocation ? null : <Option key="-1" value={-1}>{allValueName}</Option>,
					withNullableLocation ? <Option key="empty" value={NullableLocation}>{nullableLocationName}</Option> : null,
				]}
				itemsProviderSynchronizerProps={{
					objectType: 'LocationRedux',
				}}
			/>
		)
		: (
			<Autocomplete<Location>
				loadOnFocus
				{...autocompleteProps}
				params={params}
				type={loadRequest ?? 'locationViewList'}
				renderTitle="nameEn"
				onSelect={(value, option) => onChange({ currentTarget: { name: param, value } }, option?.props?.item)}
				onChange={(value) => !value && onChange({ currentTarget: { name: param, value: null } }, null)}
			/>
		);

	return withEye ? (
		<div className="input-group">
			{component}
			<div className="input-group-btn">
				<Links.Location
					id={location?.id}
					className={`btn btn-sm btn-primary ${!location ? 'disabled' : ''}`}
					title="View Location"
				>
					<i className="fa fa-eye" />
				</Links.Location>
			</div>
		</div>
	) : <>{component}</>;
};

export default LocationSelect;
